import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { BookingInfo } from "@/types/booking";

export const useSlotReservation = () => {
  const [isReserving, setIsReserving] = useState(false);
  const [reservationExpiry, setReservationExpiry] = useState<Date | null>(null);
  const [lastError, setLastError] = useState<any>(null);
  const { toast } = useToast();

  const reserveSlot = async (
    bookingInfo: BookingInfo,
    selectedDate: Date,
    selectedTime: string
  ): Promise<string | null> => {
    setIsReserving(true);
    setLastError(null);

    try {
      // Check for valid session before making the request
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          variant: "destructive",
          title: "Session Expired",
          description: "Please log in again to continue booking"
        });
        setLastError({ type: 'auth_error', error: 'No valid session' });
        return null;
      }

      const [hours, minutes] = selectedTime.split(':').map(Number);
      const startTime = new Date(selectedDate);
      startTime.setHours(hours, minutes, 0, 0);

      const endTime = new Date(startTime.getTime() + (bookingInfo.duration * 60000));

      const requestBody = {
        practitionerId: bookingInfo.practitionerId,
        serviceId: bookingInfo.serviceId,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        format: bookingInfo.format || 'virtual',
      };

      console.log('Invoking reserve-slot with:', requestBody);

      const { data, error } = await supabase.functions.invoke('reserve-slot', {
        body: requestBody
      });

      console.log('Reserve-slot response:', { data, error });

      if (error) {
        setLastError({ type: 'invoke_error', error, requestBody });
        throw error;
      }

      if (data?.error) {
        const errorDetails = {
          type: 'function_error',
          message: data.error,
          debug: data.debug,
          requestBody
        };
        setLastError(errorDetails);

        const debugInfo = data.debug ? JSON.stringify(data.debug, null, 2) : '';
        toast({
          variant: "destructive",
          title: "Reservation Failed",
          description: `${data.error}${debugInfo ? '\n\nDebug Info:\n' + debugInfo : ''}`
        });
        console.error('Reservation failed with debug info:', data.debug);
        return null;
      }

      setReservationExpiry(new Date(data.reservedUntil));

      return data.bookingId;
    } catch (error: any) {
      console.error('Slot reservation error:', error);
      if (!lastError) {
        setLastError({ type: 'exception', error: error.message });
      }
      toast({
        variant: "destructive",
        title: "Reservation Failed",
        description: error.message || "Unable to reserve this time slot"
      });
      return null;
    } finally {
      setIsReserving(false);
    }
  };

  const clearReservation = () => {
    setReservationExpiry(null);
  };

  return {
    isReserving,
    reservationExpiry,
    reserveSlot,
    clearReservation,
    lastError
  };
};
